import React, { useEffect, useRef, useState } from "react";
import { Card, CardBody, Input, Button, Row, Col } from "reactstrap";
import { getUsers, addUser, editUser, deleteUser } from "helpers/fakebackend_helper";
import AddEditUserModal from "./AddEditUserModal";
import UserDetailRow from "./UserDetailRow";
import SortableTableHeader from "pages/MonthlyParking/SortableTableHeader";

const UsersTable = ({ token }) => {
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState("");
    const [filterRole, setFilterRole] = useState("All Roles");

    const [modal, setModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [done, setDone] = useState(false);
    const [page, setPage] = useState(1);
    const [error, setError] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

    const usersPerPage = 20;
    const loadOnce = useRef(false);

    const loadUsers = async () => {
        const { data } = await getUsers(token);
        setUsers(data);
    };


    useEffect(() => {
        if (loadOnce.current) return;
        loadOnce.current = true;
        loadUsers();
    }, []);


    const roles = ["All Roles", ...new Set(users.map(u => u.role).filter(Boolean))];

    const filtered = users.filter((u) => {
        const s = search.toLowerCase();
        const matchesSearch =
            u.full_name?.toLowerCase().includes(s) ||
            u.email?.toLowerCase().includes(s);

        const matchesRole =
            filterRole === "All Roles" || u.role === filterRole;

        return matchesSearch && matchesRole;
    });

    const applyUserSort = (list) => {
        if (!sortConfig.key) return list;

        return [...list].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (sortConfig.key === 'created_at') {
                aVal = new Date(aVal || 0);
                bVal = new Date(bVal || 0);
            } else {
                if (typeof aVal === "string") aVal = aVal.toLowerCase();
                if (typeof bVal === "string") bVal = bVal.toLowerCase();
            }

            if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    };

    const sorted = applyUserSort(filtered)
    const paginated = sorted.slice((page - 1) * usersPerPage, page * usersPerPage);
    const totalPages = Math.ceil(filtered.length / usersPerPage);

    const handleSave = async () => {
        setError("");
        setIsSaving(true);

        try {
            let res;
            if (editingUser?.id) {
                res = await editUser(token, editingUser);
            } else {
                res = await addUser(token, editingUser);
            }

            if (res?.code === 0) {
                setError(res.message || "Error");
                setIsSaving(false);
                return;
            }

            setIsSaving(false);
            setDone(true);
            setTimeout(() => {
                setDone(false);
                setModal(false);
                setEditingUser(null);
                loadUsers();
            }, 1000);

        } catch (err) {
            setError(err?.response?.data?.message || "Unexpected error");
            setIsSaving(false);
        }
    };

    const onToggleModal = () => {
        setModal(!modal);
        setEditingUser(null);
        setError("");
        setIsSaving(false);
        setDone(false);
    };

    return (
        <Card className="accordion-gradient-border" style={{ background: "#3C3C3C" }}>
            <CardBody>
                <Row className="mb-3 align-items-center justify-content-between">

                    {/* Filters Group */}
                    <Col md="auto" className="d-flex align-items-center">
                        <select
                            className="form-select form-select-md w-auto accordion-gradient-border me-1"
                            style={{ textAlign: "center" }}
                            value={filterRole}
                            onChange={(e) => setFilterRole(e.target.value)}
                        >
                            {roles.map((r, i) => (
                                <option key={i}>{r}</option>
                            ))}
                        </select>

                        <Input
                            type="text"
                            placeholder="Search"
                            className="accordion-gradient-border me-1"
                            style={{ width: "180px" }}
                            value={search}
                            onChange={e => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                        />
                    </Col>

                    {/* Buttons Group */}
                    <Col md="auto" className="d-flex align-items-center gap-2">
                        <Button
                            className="refresh-gradient-bg"
                            onClick={() => {
                                setEditingUser({});
                                setModal(true);
                            }}
                        >
                            + Add CurbDash User
                        </Button>

                        <Button
                            className="export-gradient-bg"
                            onClick={() => {
                                const base = window.location.origin;
                                window.open(`${base}/onboarding/${token}`, "_blank");
                            }}
                        >
                            Onboard W2 Employee
                        </Button>
                    </Col>
                </Row>

                <div style={{ maxHeight: "60vh", overflowY: "auto", overflowX: "auto" }}>
                    <table
                        className="table align-middle table-hover table-dark table-nowrap mb-0"
                        style={{ borderCollapse: "separate", borderSpacing: 0 }}
                    >
                        <SortableTableHeader
                            columns={[
                                { label: "Picture" },
                                { label: "Full Name", sortKey: "full_name" },
                                { label: "Email", sortKey: "email" },
                                { label: "Role" },
                                { label: "Created At", sortKey: "created_at" },
                                { label: "Actions" }
                            ]}
                            onSort={setSortConfig}
                        />

                        <tbody>
                            {paginated.map((u) => (
                                <UserDetailRow
                                    key={u.id}
                                    user={u}
                                    onEdit={(user) => {
                                        setEditingUser(user);
                                        setModal(true);
                                    }}
                                    onDelete={async (id) => {
                                        await deleteUser(token, { id });
                                        loadUsers();
                                    }}
                                    reloadData={loadUsers}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="d-flex justify-content-center gap-3 mt-3">
                    <Button
                        size="sm"
                        disabled={page === 1}
                        onClick={() => setPage(p => p - 1)}
                    >
                        Previous
                    </Button>

                    <span className="text-white" style={{ fontWeight: "bold", marginTop: 5 }}>
                        {page} / {totalPages} | Total: {filtered.length}
                    </span>

                    <Button
                        size="sm"
                        disabled={page === totalPages}
                        onClick={() => setPage(p => p + 1)}
                    >
                        Next
                    </Button>
                </div>


                <AddEditUserModal
                    isOpen={modal}
                    toggle={onToggleModal}
                    editingUser={editingUser}
                    setEditingUser={setEditingUser}
                    handleSave={handleSave}
                    isSaving={isSaving}
                    done={done}
                    error={error}
                />
            </CardBody>
        </Card>
    );
};

export default UsersTable;